import type {DiffHunkAgentReference, ValidatedCommitMessage} from '@github-ui/generate-commit-message'
import {useQuery} from '@github-ui/react-query'

// Bounds how long a request's terminal result stays cached, so reopening the dialog within this
// window reuses the same attempt instead of firing a new request.
const CACHE_RETENTION_MS = 5 * 60 * 1000
const REQUEST_DEADLINE_MS = 60_000

export type SuggestionGenerationConfig = {
  /** Stable identifier for the suggestion, used as part of the cache key so reopening the dialog does not re-request. */
  automatedCommentId: string
  /** Prebuilt `github.diff-hunk` references reconstructed from the suggestion's existing diff entries. */
  diffHunks: DiffHunkAgentReference[]
  /**
   * Backend-provided CAPI base path authorizing this request. This is expected to come from the
   * comment's own backend-provided generation config (server-decided kill switch, entitlement,
   * head-branch pattern restriction, and SKU endpoint) rather than a default public CAPI host, so
   * callers should omit `generationConfig` entirely rather than pass an empty `basePath` when that
   * backend config is absent.
   */
  basePath: string
  /** Backend-provided SSO organization IDs to scope the Copilot auth token to. */
  ssoOrgIds: string[]
}

export type SuggestionCommitMessageGenerationTerminalState = {
  status: 'success' | 'no-title' | 'error'
  durationMs: number
}

export type SuggestionCommitMessageGenerationState =
  | {status: 'disabled' | 'idle' | 'loading'}
  | SuggestionCommitMessageGenerationTerminalState

type GenerationQueryData = {
  validatedMessage?: ValidatedCommitMessage
  terminal: SuggestionCommitMessageGenerationTerminalState
}

type UseGenerateSuggestionCommitMessageArgs = {
  commentKind: 'ccr' | 'human' | 'mixed'
  generationConfig?: SuggestionGenerationConfig
  /**
   * Called once per executed request, from inside the queryFn as it settles. The callback is
   * captured when that request starts and may run before TanStack writes the terminal result to
   * the cache, and after its consumer unmounts, so it must be side-effect-only. It is queued in a
   * microtask so a callback error cannot change the request's cached outcome. Cached reads and
   * disabled requests never invoke it.
   */
  onGenerationSettled?: (generationState: SuggestionCommitMessageGenerationTerminalState) => void
}

/**
 * Predicate for whether `useGenerateSuggestionCommitMessage` should run a request: `commentKind`
 * must be `'ccr'`, and `generationConfig` must carry a backend-authorized `basePath` and at least
 * one diff hunk.
 */
export function isSuggestionGenerationApplicable({
  commentKind,
  generationConfig,
}: Pick<UseGenerateSuggestionCommitMessageArgs, 'commentKind' | 'generationConfig'>): boolean {
  return commentKind === 'ccr' && Boolean(generationConfig?.basePath) && (generationConfig?.diffHunks.length ?? 0) > 0
}

/**
 * Requests a generated commit message for a CCR automated-suggestion "Apply suggestion" dialog,
 * reusing the shared `@github-ui/generate-commit-message` request core.
 *
 * The queryFn never reads TanStack's `signal`, so observer unmounts do not cancel the request.
 * Instead, the request uses an independent deadline and caches its terminal result (`success` |
 * `no-title` | `error`) for reuse within `CACHE_RETENTION_MS`. The dynamic `import()` keeps the
 * request core out of this bundle until a CCR suggestion actually needs it.
 */
export function useGenerateSuggestionCommitMessage({
  commentKind,
  generationConfig,
  onGenerationSettled,
}: UseGenerateSuggestionCommitMessageArgs) {
  const diffHunks = generationConfig?.diffHunks ?? []
  const basePath = generationConfig?.basePath ?? ''
  const ssoOrgIds = generationConfig?.ssoOrgIds ?? []
  const enabled = isSuggestionGenerationApplicable({commentKind, generationConfig})

  const {data, fetchStatus} = useQuery<GenerationQueryData>({
    queryKey: [
      'generate-suggestion-commit-message',
      generationConfig?.automatedCommentId,
      diffHunks,
      basePath,
      ssoOrgIds,
    ],
    queryFn: async (): Promise<GenerationQueryData> => {
      const startedAt = performance.now()
      const signal = AbortSignal.timeout(REQUEST_DEADLINE_MS)
      const settle = (
        terminal: SuggestionCommitMessageGenerationTerminalState,
        validatedMessage?: ValidatedCommitMessage,
      ): GenerationQueryData => {
        if (onGenerationSettled) queueMicrotask(() => onGenerationSettled(terminal))
        return {validatedMessage, terminal}
      }

      try {
        const {requestGeneratedCommitMessage} = await import('@github-ui/generate-commit-message')
        const {validatedMessage} = await requestGeneratedCommitMessage({diffHunks, basePath, ssoOrgIds, signal})
        return settle(
          {status: validatedMessage ? 'success' : 'no-title', durationMs: Math.round(performance.now() - startedAt)},
          validatedMessage,
        )
      } catch {
        return settle({status: 'error', durationMs: Math.round(performance.now() - startedAt)})
      }
    },
    enabled,
    staleTime: Infinity,
    gcTime: CACHE_RETENTION_MS,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    meta: {action: 'get-generated-suggestion-commit-message'},
  })

  const generationState: SuggestionCommitMessageGenerationState = !enabled
    ? {status: 'disabled'}
    : (data?.terminal ?? (fetchStatus === 'fetching' ? {status: 'loading'} : {status: 'idle'}))

  return {
    generatedCommitMessage: enabled && data?.terminal.status === 'success' ? data.validatedMessage : undefined,
    generationState,
  }
}
